import type { CheckinItemReceivingStatus, EnrichedCheckinItem } from "./types";
import { checkinCompletionPercentage } from "./completion";

export type ReceivingSummary = {
  status: CheckinItemReceivingStatus;
  expectedQuantity: number;
  receivedTotal: number;
  pendingQuantity: number;
  surplusQuantity: number;
  progressPercent: number;
  isShortfallClosed: boolean;
};

function legacyReceivingStatus(
  item: EnrichedCheckinItem,
): CheckinItemReceivingStatus {
  if (item.receiving_status) return item.receiving_status;
  if (item.status === "OK") return "COMPLETE";
  if (item.status === "INCONGRUENT") return "PARTIAL";
  if (item.status === "NOT_ARRIVED") return "SHORTFALL_CLOSED";
  return "PENDING";
}

function getReceivedTotal(item: EnrichedCheckinItem) {
  if (item.received_total !== undefined) return item.received_total;
  if (item.received_quantity > 0) return item.received_quantity;
  if (item.status === "OK" && item.expected_quantity > 0) {
    return item.expected_quantity;
  }
  return item.received_quantity ?? 0;
}

function deriveReceivingStatus({
  item,
  receivedTotal,
  expectedQuantity,
  isShortfallClosed,
}: {
  item: EnrichedCheckinItem;
  receivedTotal: number;
  expectedQuantity: number;
  isShortfallClosed: boolean;
}): CheckinItemReceivingStatus {
  if (isShortfallClosed) return "SHORTFALL_CLOSED";
  if (expectedQuantity > 0 && receivedTotal > expectedQuantity) {
    return "SURPLUS";
  }
  if (expectedQuantity > 0 && receivedTotal === expectedQuantity) {
    return "COMPLETE";
  }
  if (receivedTotal > 0) return "PARTIAL";
  return legacyReceivingStatus(item);
}

export function buildReceivingSummary(
  item: EnrichedCheckinItem,
): ReceivingSummary {
  const expectedQuantity =
    item.active_expected_quantity ?? item.expected_quantity ?? 0;
  const receivedTotal = getReceivedTotal(item);
  const pendingQuantity =
    item.pending_quantity ?? Math.max(expectedQuantity - receivedTotal, 0);
  const surplusQuantity =
    item.surplus_quantity ?? Math.max(receivedTotal - expectedQuantity, 0);
  const isShortfallClosed =
    !!item.is_shortfall_closed ||
    item.receiving_status === "SHORTFALL_CLOSED" ||
    item.status === "NOT_ARRIVED";
  const status = deriveReceivingStatus({
    item,
    receivedTotal,
    expectedQuantity,
    isShortfallClosed,
  });
  const progressPercent = checkinCompletionPercentage(
    receivedTotal,
    expectedQuantity,
  );

  return {
    status,
    expectedQuantity,
    receivedTotal,
    pendingQuantity,
    surplusQuantity,
    progressPercent,
    isShortfallClosed,
  };
}
